import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/AuthContext';

interface SidebarLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, icon, label, isActive }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
      isActive
        ? 'bg-[#3A5E63] text-white'
        : 'text-[#344854] hover:bg-[#E8F5F2]'
    }`}
  >
    <span className="text-xl">{icon}</span>
    <span>{label}</span>
  </Link>
);

const CrewLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get display name from user profile or email
  const getUserDisplayName = () => {
    if (userProfile?.full_name) {
      return userProfile.full_name;
    } else if (user?.email) {
      return user.email.split('@')[0];
    } else {
      return 'Crew Member';
    }
  };

  // Get first letter for avatar
  const getAvatarLetter = () => {
    const displayName = getUserDisplayName();
    return displayName.charAt(0).toUpperCase();
  };

  return (
    <div className="flex h-[calc(100vh-5rem)] bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`fixed top-20 bottom-0 left-0 z-10 flex flex-col bg-white shadow-lg transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:relative md:top-auto md:bottom-auto md:h-full md:translate-x-0 md:w-64`}
      >
        {/* Crew Panel Title */}
        <div className="p-5 border-b">
          <div className="font-medium text-lg text-[#344854]">Crew Panel</div>
        </div>

        {/* Navigation links */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          <SidebarLink
            to="/crew/dashboard"
            icon={<span>📊</span>}
            label="Dashboard"
            isActive={location.pathname === '/crew/dashboard'}
          />
          <SidebarLink
            to="/crew/schedule"
            icon={<span>📅</span>}
            label="My Schedule"
            isActive={location.pathname.startsWith('/crew/schedule')}
          />
          <SidebarLink
            to="/crew/profile"
            icon={<span>👤</span>}
            label="My Profile"
            isActive={location.pathname.startsWith('/crew/profile')}
          />
          <SidebarLink
            to="/crew/messages"
            icon={<span>💬</span>}
            label="Messages"
            isActive={location.pathname.startsWith('/crew/messages')}
          />
          <SidebarLink
            to="/crew/users"
            icon={<span>👥</span>}
            label="Users"
            isActive={location.pathname.startsWith('/crew/users')}
          />

          <div className="pt-4 border-t mt-8">
            <SidebarLink
              to="/"
              icon={<span>🏠</span>}
              label="View Website"
              isActive={false}
            />
          </div>
        </nav>

        {/* User profile */}
        <div className="p-4 border-t">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#3A5E63] text-white flex items-center justify-center font-medium">
              {user && getAvatarLetter()}
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{user && getUserDisplayName()}</div>
              <div className="text-xs text-[#344854]/70">Crew Member</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile menu button for small screens */}
        <div className="md:hidden p-2 bg-white border-b flex items-center">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-[#344854] focus:outline-none"
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            <span className="text-xl">{sidebarOpen ? '✖️' : '☰'}</span>
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 pt-6 bg-gray-50">
          {children}
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-0 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default CrewLayout;
